import React, { useState, useRef, useEffect, useCallback } from 'react';
import Dropzone from 'react-dropzone';
import BEM from '../../../../bem';
import { Modal } from '../../..';
import { ReactComponent as UploadButtonSvg } from '../../../../images/upload.svg';
import mobxInjectSelect from '../../../../utils/mobxInjectSelect';
import TCClient from '../../../../../client';
import { useAppSelector } from 'redux-stores';
import { DropzoneRef } from 'types/DropzoneRef';

const classes = BEM.with('UploadPBXLinesModal');

type PBXSystem = {
  domain: string;
  id: number;
  organizationId: number;
  targetType: string;
};

type UploadPBXCsvModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  uploadType: string;
  header: string;
  title: string;
  description: string;
  dataTestId: string;
};

type MobxProps = {
  uploadCSV: (
    newFile: File,
    type: string,
    { systemConfigurationId }: { systemConfigurationId: string }
  ) => void;
};

function UploadPBXCsvModal({
  isOpen,
  closeModal,
  uploadCSV,
  uploadType,
  header,
  title,
  description,
  dataTestId,
}: UploadPBXCsvModalProps & MobxProps) {
  const [error, setError] = useState<string | null>(null);
  const file = useRef<File | ''>('');
  const dropzoneRef = useRef<DropzoneRef>(null);
  const [pbxSystems, setPbxSystems] = useState<PBXSystem[] | null>([]);
  const [selectedSystemIdValue, setSelectedSystemIdValue] = useState('');

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSystemIdValue(event.target.value);
    setError(null);
  };

  const handleRequestClose = () => {
    setError(null);
    file.current = '';
    closeModal();
  };

  const currentOrganizationId = useAppSelector((state) => state.orgSettings.settings.id);

  const getPBXSystems = useCallback(async () => {
    const containNurseCall = false;
    try {
      const pbxSystems = (await TCClient.pbx.getPbxSystems({
        currentOrganizationId,
        containNurseCall,
      })) as PBXSystem[];
      setPbxSystems(pbxSystems);
    } catch (error) {
      console.error('Error fetching PBX systems:', error);
    }
  }, [currentOrganizationId]);

  useEffect(() => {
    if (isOpen) {
      getPBXSystems();
    }
  }, [isOpen, getPBXSystems]);

  const handleUploadClick = () => {
    if (selectedSystemIdValue === '') {
      setError('Please select a PBX System ID and retry upload');
      return;
    }
    dropzoneRef.current?.open();
  };

  const handleFilesAccepted = async (files: File[]) => {
    const newFile = files[0];
    if (!newFile) return;

    if (selectedSystemIdValue === '') {
      setError('Please select a PBX System ID and retry upload');
      return;
    }
    file.current = newFile;
    await uploadCSV(newFile, uploadType, { systemConfigurationId: selectedSystemIdValue });
  };

  return (
    <Modal
      bodyClass={classes('body')}
      className={classes()}
      header={header}
      closeClass={classes('close-button')}
      isOpen={isOpen}
      onRequestClose={handleRequestClose}
      size={'medium-large'}
    >
      <div className={classes('main-container')}>
        {error && (
          <div className={classes('error')}>
            <div className={classes('error-message')}>{error}</div>
          </div>
        )}
        <div className={classes('pbx-system-id-container')}>
          <div className={classes('pbx-system-id-label')}>Select PBX System:</div>
          <div>
            <select id="dropdown" value={selectedSystemIdValue} onChange={handleChange}>
              <option value="" disabled>
                Select a system
              </option>
              {pbxSystems &&
                pbxSystems.map((option, index) => (
                  <option key={index} value={option.id}>
                    {`${option.domain}/${option.targetType}`}
                  </option>
                ))}
            </select>
          </div>
        </div>
        <Dropzone
          ref={dropzoneRef}
          className={classes('dropzone')}
          disableClick
          disablePreview
          multiple={false}
          accept="text/csv"
          onDropAccepted={handleFilesAccepted}
        >
          {({ isDragActive = false } = {}) => (
            <div
              className={classes('upload-button-container', { dragActive: isDragActive })}
              data-test-id={dataTestId}
              onClick={handleUploadClick}
            >
              <div className={classes('column-one')}>
                <div className={classes('first-row')}>{title}</div>
                <div className={classes('second-row')}>{description}</div>
                <div className={classes('drag-hint')}>or drag and drop a .csv file here</div>
              </div>
              <div className={classes('column-two')}>
                <UploadButtonSvg className={classes('button-icon-path')} />
              </div>
            </div>
          )}
        </Dropzone>
      </div>
    </Modal>
  );
}

export default mobxInjectSelect<UploadPBXCsvModalProps, MobxProps>({
  patientStore: ['uploadCSV'],
})(UploadPBXCsvModal);
